import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Eye, Plus, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import DeeplinkTargetFields, { DeeplinkTargetType, getDeeplinkTargetDisplay } from './DeeplinkTargetFields';

const DeeplinksEditor = () => {
    const navigate = useNavigate();
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [slugDraft, setSlugDraft] = useState('');
    const [targetTypeDraft, setTargetTypeDraft] = useState<DeeplinkTargetType>('ITEM');
    const [bookableItemIdDraft, setBookableItemIdDraft] = useState('');
    const [categoryIdDraft, setCategoryIdDraft] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    const utils = trpc.useUtils();

    const { data: deeplinksData, isLoading } = trpc.bookingFunnelDeeplinks.getAll.useQuery();
    const { data: bookableData } = trpc.bookingFunnelDeeplinks.getBookableItemsForSelect.useQuery(undefined, {
        enabled: isCreateDialogOpen,
    });
    const { data: categoriesData } = trpc.bookingFunnelDeeplinks.getCategoriesForSelect.useQuery(undefined, {
        enabled: isCreateDialogOpen,
    });

    const createMutation = trpc.bookingFunnelDeeplinks.create.useMutation({
        onSuccess: (result) => {
            toast.success('Deeplink erstellt');
            utils.bookingFunnelDeeplinks.getAll.invalidate();
            setIsCreateDialogOpen(false);
            setSlugDraft('');
            setTargetTypeDraft('ITEM');
            setBookableItemIdDraft('');
            setCategoryIdDraft('');
            navigate(`/cms/deeplinks/${result.data.id}`);
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const deeplinks = deeplinksData?.data ?? [];
    const bookableItems = bookableData?.data ?? [];
    const categories = categoriesData?.data ?? [];

    const filtered = useMemo(() => {
        if (!searchQuery.trim()) return deeplinks;
        const q = searchQuery.toLowerCase();
        return deeplinks.filter((d) => {
            const target = getDeeplinkTargetDisplay(d);
            return (
                d.slug.toLowerCase().includes(q) ||
                target.nameDe.toLowerCase().includes(q) ||
                target.nameEn.toLowerCase().includes(q)
            );
        });
    }, [deeplinks, searchQuery]);

    const submitCreate = () => {
        if (!slugDraft.trim()) {
            toast.error('Bitte geben Sie einen Slug ein');
            return;
        }
        if (targetTypeDraft === 'ITEM' && !bookableItemIdDraft) {
            toast.error('Bitte wählen Sie eine buchbare Leistung');
            return;
        }
        if (targetTypeDraft === 'CATEGORY' && !categoryIdDraft) {
            toast.error('Bitte wählen Sie eine Kategorie');
            return;
        }
        createMutation.mutate({
            slug: slugDraft.trim(),
            bookableItemId: targetTypeDraft === 'ITEM' ? bookableItemIdDraft : null,
            categoryId: targetTypeDraft === 'CATEGORY' ? categoryIdDraft : null,
        });
    };

    if (isLoading) {
        return (
            <div className='space-y-4'>
                <Skeleton className='h-10 w-48' />
                <Skeleton className='h-64 w-full' />
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            <div className='flex justify-between items-center'>
                <p className='text-sm text-muted-foreground'>
                    Jeder Deeplink hat einen eindeutigen Slug und verweist auf eine buchbare Leistung oder eine Kategorie. Mehrere Deeplinks pro Ziel sind möglich.
                </p>
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                    <Plus className='mr-2 h-4 w-4' />
                    Neuer Deeplink
                </Button>
            </div>

            {deeplinks.length === 0 ? (
                <div className='bg-white rounded-lg shadow-sm p-6'>
                    <div className='py-12 text-center text-muted-foreground'>
                        Noch keine Deeplinks. Klicken Sie auf &quot;Neuer Deeplink&quot;, um zu beginnen.
                    </div>
                </div>
            ) : (
                <div className='bg-white rounded-lg shadow-sm p-6 space-y-4'>
                    <div className='relative'>
                        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                        <Input
                            placeholder='Nach Slug, Leistung oder Kategorie suchen…'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className='pl-9'
                        />
                        {searchQuery && (
                            <Button
                                variant='ghost'
                                size='icon'
                                className='absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7'
                                onClick={() => setSearchQuery('')}
                            >
                                <X className='h-3 w-3' />
                            </Button>
                        )}
                    </div>
                    <div className='rounded-md border border-gray-200 overflow-hidden'>
                        <Table>
                            <TableHeader className='bg-gray-50'>
                                <TableRow>
                                    <TableHead className='font-medium text-gray-700'>Slug</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Ziel</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Typ</TableHead>
                                    <TableHead className='font-medium text-gray-700'>Behandler</TableHead>
                                    <TableHead className='font-medium text-gray-700 text-right'>Aktionen</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((row) => {
                                    const target = getDeeplinkTargetDisplay(row);
                                    return (
                                        <TableRow
                                            key={row.id}
                                            className='hover:bg-gray-50 transition-colors cursor-pointer'
                                            onClick={() => navigate(`/cms/deeplinks/${row.id}`)}
                                        >
                                            <TableCell className='font-mono text-sm'>{row.slug}</TableCell>
                                            <TableCell>
                                                <div className='flex flex-col'>
                                                    <span className='font-medium'>{target.nameDe}</span>
                                                    <span className='text-xs text-muted-foreground'>{target.nameEn}</span>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant='secondary' className='text-xs'>
                                                    {target.typeLabel}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className='text-sm text-muted-foreground'>
                                                {row.practitioner
                                                    ? `${row.practitioner.title} ${row.practitioner.firstName} ${row.practitioner.lastName}`
                                                    : '—'}
                                            </TableCell>
                                            <TableCell className='text-right'>
                                                <Button
                                                    variant='ghost'
                                                    size='icon'
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/cms/deeplinks/${row.id}`);
                                                    }}
                                                    title='Bearbeiten'
                                                >
                                                    <Eye className='h-4 w-4' />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}

            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent className='max-w-lg'>
                    <DialogHeader>
                        <DialogTitle>Neuen Deeplink erstellen</DialogTitle>
                        <DialogDescription>
                            Der Slug erscheint in der URL. Nur Kleinbuchstaben, Ziffern und Bindestriche.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='space-y-4'>
                        <div className='space-y-2'>
                            <Label>Slug</Label>
                            <Input
                                placeholder='z.B. sommer-aktion'
                                value={slugDraft}
                                onChange={(e) => setSlugDraft(e.target.value)}
                            />
                        </div>
                        <DeeplinkTargetFields
                            targetType={targetTypeDraft}
                            onTargetTypeChange={setTargetTypeDraft}
                            bookableItems={bookableItems}
                            bookableItemId={bookableItemIdDraft}
                            onBookableItemIdChange={setBookableItemIdDraft}
                            categories={categories}
                            categoryId={categoryIdDraft}
                            onCategoryIdChange={setCategoryIdDraft}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => setIsCreateDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={submitCreate} disabled={createMutation.isPending}>
                            Erstellen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DeeplinksEditor;
